import { useQuery } from "@apollo/client";
import {
  Card,
  CircularProgress,
  Collapse,
  CssBaseline,
  Fade,
  Grid,
  Icon,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import brand from "assets/images/logo.png";
import theme from "assets/theme";
import borders from "assets/theme/base/borders";
import boxShadows from "assets/theme/base/boxShadows";
import ErrorMessager from "components/ErrorMessager";
import AdminLicenseMonitor from "components/AdminLicenseMonitor";
import Sidenav from "components/Sidenav";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { MessageManager, setMiniSidenav, useSoftUIController } from "context";
import { ConfirmarProvider } from "context/ConfirmarContext";
import { MenuProvider } from "context/menuContext";
import { UserContext } from "context/user";
import { useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ModalCompletarDatos from "components/ModalCompletarDatos";
import routes from "routes";
import { GET_USER } from "services/login";
import {
  RUTA_VENTA_EVENTO,
  esRutaVentaEvento as esPathVentaEvento,
  esVendedorSoloVentaEvento as esSoloVentaEvento,
  puedeAccederRuta,
} from "utils/accesoRutas";
export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { snackbar, snackbarMessage, snackbarType, handleSnackbar, closeSnackbar } =
    useContext(MessageManager);
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  // El POS y su pantalla al cliente comparten la excepción de demo local.
  const esRutaVentaEvento = ["/venta-evento", "/venta-evento/pantalla-cliente"].includes(pathname);
  const isVentaEventoDemo =
    // Create React App inyecta las variables REACT_APP_* durante el build.
    // eslint-disable-next-line no-undef
    process.env.REACT_APP_VENTA_EVENTO_DEMO === "true" && esRutaVentaEvento;
  const [loadingSpinner, setLoadingSpinner] = useState(true);
  const { borderRadius } = borders;
  const { lg } = boxShadows;
  const [snackbarInterval, setSnackbarInterval] = useState(null);
  const [openModalCompletarDatos, setOpenModalCompletarDatos] = useState(false);

  // useEffect(() => {

  //   const mantenimientoFlag = localStorage.getItem("mantenimientoFlag");
  //   if (!mantenimientoFlag && pathname !== "/mantenimiento") {
  //     window.location.href = "/mantenimiento";
  //   }
  // }, []);

  useEffect(() => {
    if (snackbar) {
      if (snackbarInterval) {
        clearTimeout(snackbarInterval);
      }
      setSnackbarInterval(
        setTimeout(() => {
          closeSnackbar();
        }, 6000)
      );
    }
  }, [snackbar]);

  const { data, loading, error, refetch } = useQuery(GET_USER, { skip: isVentaEventoDemo });

  const validatePath = (route, pathname) => {
    if (route.includes(":")) {
      let routeArray = route.split("/");
      let pathnameArray = pathname.split("/");
      if (
        routeArray.length === pathnameArray.length &&
        pathnameArray[pathnameArray.length - 1] !== ""
      ) {
        for (let i = 0; i < routeArray.length; i++) {
          if (routeArray[i] !== pathnameArray[i] && !routeArray[i].includes(":")) {
            return false;
          }
        }
        return true;
      }
    }
    return false;
  };

  const currentRoute = routes.find(
    (route) => route.route === pathname || validatePath(route.route, pathname)
  );
  const { handleUser, logout } = useContext(UserContext);

  useEffect(() => {
    if (isVentaEventoDemo) {
      handleUser({
        id: "demo-vendedor-evento",
        username: "vendedor.evento",
        nombre: "Marina",
        apellido: "López",
        tipoUser: "Administrador",
        permisos: [{ pantalla: "Pedidos" }],
      });
    }
    // El usuario simulado sólo existe al iniciar explícitamente el modo demo local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVentaEventoDemo]);

  useEffect(() => {
    // QA2-12: con solo el permiso 71 no se abre ninguna otra ruta, tampoco las
    // pantallas públicas excluidas más abajo.
    if (
      !isVentaEventoDemo &&
      data?.currentUser &&
      esSoloVentaEvento(data.currentUser) &&
      !esPathVentaEvento(pathname)
    ) {
      handleUser(data.currentUser);
      window.location.replace(RUTA_VENTA_EVENTO);
      return;
    }
    if (
      !isVentaEventoDemo &&
      pathname !== "/carta" &&
      pathname !== "/cocina" &&
      pathname !== "/ordenes-trabajo" &&
      pathname !== "/recepcion" &&
      // pathname !== "/cajaRecepcion/:destino?" &&
      pathname.includes("/cajaRecepcion") === false &&
      pathname !== "/bandejasRecepcion" &&
      pathname !== "/formulario" &&
      pathname !== "/tv-menu-diario" &&
      pathname !== "/mantenimiento" &&
      pathname !== "/proveedores" &&
      !pathname.includes("/pdf/")
    ) {
      if (data?.currentUser) {
        handleUser(data.currentUser);

        if (!currentRoute && pathname !== "/") {
          logout();
          return;
        }

        if (["Convenio", "Empleado", "Administrador"].includes(data?.currentUser?.tipoUser)) {
          if (!data.currentUser?.telefono) {
            setOpenModalCompletarDatos(true);
          }
        }

        if (data?.currentUser?.tipoUser === "Administrador") {
          console.log("---------- ADMIN MODE ----------");
          if (pathname === "/") {
            window.location.href = "/usuarios";
          }
          return;
        }

        if (
          (currentRoute?.permiso &&
            !data?.currentUser?.permisos?.find(
              (item) => item.pantalla === currentRoute?.permiso
            )) ||
          pathname === "/"
        ) {
          let rutasPrivadas = routes.filter((route) => route.permiso);
          let rutasPermitidas = rutasPrivadas.filter((route) =>
            data?.currentUser?.permisos?.find((item) => item.pantalla === route.permiso)
          );
          if (rutasPermitidas.length > 0) {
            handleSnackbar(
              "No tiene permisos para acceder a esta pantalla, será redirigido...",
              "error"
            );
            window.location.href = rutasPermitidas[0].route;
            return;
          } else {
            logout();
            return;
          }
        }
      } else if (error) {
        if (pathname !== "/login" && pathname !== "/totem" && pathname !== "/sistema-comedor" && pathname !== "/successTotem" && pathname !== "/errorTotem" && pathname !== "/successComedor" && pathname !== "/errorComedor") {
          localStorage.setItem("history", JSON.stringify(pathname));
          handleSnackbar(
            "Se ha vencido la sesión, deberá iniciar sesión nuevamente. Será redirigido...",
            "error"
          );
          logout();
        }
        return;
      }
    }
  }, [pathname, data]);

  useEffect(() => {
    if (!isVentaEventoDemo) refetch();
  }, [pathname, isVentaEventoDemo, refetch]);

  //set loading spinner to false
  useEffect(() => {
    if (!loading) {
      setLoadingSpinner(false);
    }
  }, [loading]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Regla de acceso en utils/accesoRutas.js: Administrador ve todo (incluido el
  // POS sin el permiso 71); el resto, por pantalla; con solo VentaEvento, solo el POS.
  const canAccessRoute = (route, currentUser = data?.currentUser) =>
    (isVentaEventoDemo && route?.route === pathname) || puedeAccederRuta(route, currentUser);

  // Un vendedor de evento dedicado (sin ningún otro permiso, cualquiera sea su
  // tipo de usuario) usa la pantalla como kiosco: sin sidenav, igual que /sistema-comedor.
  const esVendedorSoloVentaEvento = esSoloVentaEvento(data?.currentUser);

  const firstAllowedPrivateRoute = routes.find(
    (route) => route.route && route.permiso && canAccessRoute(route)
  );

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const element = canAccessRoute(route)
          ? route.component
          : <Navigate to={firstAllowedPrivateRoute?.route || "/login"} replace />;
        return (
          <Route exact path={route.route} element={element} key={"route-" + route.key} />
        );
      }

      return null;
    });

  return loadingSpinner ? (
    <ThemeProvider theme={theme}>
      <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Fade in={loadingSpinner} appear={true} timeout={1000}>
          <CircularProgress />
        </Fade>
      </SoftBox>
    </ThemeProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <ConfirmarProvider>
        <MenuProvider>
          {/* {device.device.type !== "desktop" && installOpen && (
            <InstallPWA setInstallOpen={setInstallOpen} />
          )} */}
          <ModalCompletarDatos
            open={openModalCompletarDatos}
            handleClose={() => setOpenModalCompletarDatos(false)}
            refetchParent={refetch}
          />
          <AdminLicenseMonitor
            isAdministrator={data?.currentUser?.tipoUser === "Administrador"}
          />
          <SoftBox
            mb={3}
            mr={3}
            sx={{
              position: "fixed",
              right: 0,
              bottom: 0,
              zIndex: 2000,
              boxShadow: lg,
              borderRadius: borderRadius.lg,
            }}
          >
            <Collapse in={snackbar} timeout={500}>
              <Card>
                <SoftBox
                  sx={{
                    width: { xs: "90vw", sm: "300px" },
                  }}
                  p={2}
                  onMouseEnter={() => clearTimeout(snackbarInterval)}
                  onMouseLeave={() => {
                    setSnackbarInterval(
                      setTimeout(() => {
                        closeSnackbar();
                      }, 3000)
                    );
                  }}
                >
                  <SoftBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="start"
                    textAlign="start"
                  >
                    <SoftBox display="flex-row" alignItems="center">
                      <SoftBox lineHeight={1} display="flex" alignItems="center">
                        <SoftBox mr={1}>
                          <Icon color={snackbarType} fontSize="small">
                            {snackbarType === "error"
                              ? "error"
                              : snackbarType === "success"
                                ? "check_circle"
                                : snackbarType === "warning"
                                  ? "warning"
                                  : "info"}
                          </Icon>
                        </SoftBox>

                        <SoftTypography variant="h7" fontWeight="bold" color={snackbarType}>
                          {snackbarType === "error"
                            ? "Error"
                            : snackbarType === "success"
                              ? "Éxito"
                              : snackbarType === "warning"
                                ? "Atención"
                                : "Información"}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox display="flex">
                      <Tooltip title="Cerrar" placement="top">
                        <Icon
                          fontSize="medium"
                          onClick={closeSnackbar}
                          sx={{
                            cursor: "pointer",
                            zIndex: 9999,
                          }}
                        >
                          close
                        </Icon>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SoftBox
                        sx={{
                          lineHeight: 1,
                        }}
                      >
                        <SoftTypography variant="caption" sx={{ whiteSpace: "pre-line" }}>
                          {snackbarMessage}
                        </SoftTypography>
                      </SoftBox>
                    </Grid>
                  </Grid>
                </SoftBox>
              </Card>
            </Collapse>
          </SoftBox>

          <CssBaseline />
          {layout === "dashboard" &&
            currentRoute !== undefined &&
            pathname !== "/totem" &&
            pathname !== "/successTotem" &&
            pathname !== "/successComedor" &&
            pathname !== "/errorTotem" &&
            pathname !== "/errorComedor" &&
            pathname !== "/tv-menu-diario" &&
            pathname !== "/carta" &&
            pathname !== "/login" &&
            pathname !== "/formulario" &&
            pathname !== "/comedorAlumnos" &&
            pathname !== "/mantenimiento" &&
            pathname !== "/proveedores" &&
            pathname !== "/sistema-comedor" &&
            // La pantalla al cliente mira al comprador: nunca lleva sidenav.
            pathname !== "/venta-evento/pantalla-cliente" &&
            !(pathname === "/venta-evento" && esVendedorSoloVentaEvento) && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={brand}
                  brandName="Kaizen Dashboard"
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />

                <ErrorMessager />
              </>
            )}
          <Routes>{getRoutes(routes)}</Routes>
        </MenuProvider>
      </ConfirmarProvider>
    </ThemeProvider>
  );
}
